import React from 'react';
import { getProductImage } from '../../utils/productImage';
import { useLanguage } from '../../context/LanguageContext';
import './ProductThumbnail.css';

const normalizeValue = (value) => String(value || '').trim().toLowerCase();

const getProductKind = (product) => {
  const text = normalizeValue(`${product.category || ''} ${product.title || product.name || ''} ${product.brand || ''}`);

  if (/watch|rolex|omega|seamaster|submariner/.test(text)) return 'watch';
  if (/eyewear|sunglass|glasses|ray-ban|oakley|prada/.test(text)) return 'eyewear';
  if (/perfume|chanel|fragrance/.test(text)) return 'perfume';

  return 'generic';
};

const getWatchTone = (product) => {
  const text = normalizeValue(`${product.title || product.name || ''} ${product.brand || ''}`);

  if (/omega|silver|seamaster/.test(text)) return 'silver';
  if (/rolex|black|submariner/.test(text)) return 'black';

  return 'gold';
};

const WatchFallback = ({ product }) => {
  const tone = getWatchTone(product);
  const palette = {
    gold: ['#a76a2d', '#d8a042', '#f5d990', '#3f3326'],
    silver: ['#cdd1d6', '#8f969f', '#f6f7f8', '#20252d'],
    black: ['#2f2f31', '#101012', '#5d6269', '#f2f2f2'],
  }[tone];

  return (
    <svg viewBox="0 0 120 120" aria-hidden="true">
      <rect x="47" y="3" width="26" height="38" rx="10" fill={palette[0]} />
      <rect x="47" y="79" width="26" height="38" rx="10" fill={palette[0]} />
      <rect x="33" y="33" width="54" height="54" rx="15" fill={palette[1]} />
      <circle cx="60" cy="60" r="21" fill={palette[2]} stroke="rgba(0,0,0,0.18)" strokeWidth="3" />
      <path d="M60 47v14l10 6" stroke={palette[3]} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="87" y="52" width="8" height="16" rx="4" fill={palette[1]} />
    </svg>
  );
};

const EyewearFallback = () => (
  <svg viewBox="0 0 180 110" aria-hidden="true">
    <g transform="translate(12 24) rotate(6 78 28)">
      <rect x="18" y="18" width="46" height="32" rx="10" fill="#4f433c" stroke="#8b582f" strokeWidth="8" />
      <rect x="70" y="18" width="46" height="32" rx="10" fill="#4f433c" stroke="#8b582f" strokeWidth="8" />
      <rect x="62" y="26" width="10" height="6" rx="3" fill="#8b582f" />
      <path d="M116 22c18-2 27 4 37 14" stroke="#8b582f" strokeWidth="7" strokeLinecap="round" />
      <path d="M18 22C2 17-5 23-14 35" stroke="#8b582f" strokeWidth="7" strokeLinecap="round" />
    </g>
    <ellipse cx="86" cy="88" rx="58" ry="12" fill="rgba(17,17,17,0.08)" />
  </svg>
);

const GenericFallback = () => (
  <svg viewBox="0 0 120 120" aria-hidden="true">
    <rect x="28" y="26" width="64" height="68" rx="16" fill="#bf57ea" opacity="0.16" />
    <path d="M35 47h50M35 62h50M35 77h34" stroke="#bf57ea" strokeWidth="7" strokeLinecap="round" />
  </svg>
);

const ProductThumbnail = ({ product = {}, size = 'cart', className = '' }) => {
  const { t } = useLanguage();
  const productName = product.title || product.name || t('common.product');
  const image = getProductImage(product);
  const kind = getProductKind(product);

  return (
    <div className={`product-thumbnail product-thumbnail--${size} ${className}`.trim()}>
      {image ? (
        <img src={image} alt={productName} />
      ) : (
        <div className={`product-thumbnail__fallback product-thumbnail__fallback--${kind}`} role="img" aria-label={productName}>
          {kind === 'watch' && <WatchFallback product={product} />}
          {kind === 'eyewear' && <EyewearFallback />}
          {kind !== 'watch' && kind !== 'eyewear' && <GenericFallback />}
        </div>
      )}
    </div>
  );
};

export default ProductThumbnail;
